import { Suspense, useState } from 'react';
import { SCREENS, telaAtiva, type SessionUser } from '@apen/shared';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useLogout, useSession } from '../../hooks/useSession';
import { useFeedback } from '../Feedback/FeedbackProvider';
import { NavIcon } from './NavIcon';
import { AccountDrawer } from './AccountDrawer';
import { Avatar } from '../Avatar/Avatar';
import { LiquidLoader } from '../Loader/LiquidLoader';
import { useTransicaoDeTela } from '../../hooks/useTransicaoDeTela';
import s from './AppShell.module.css';

/**
 * Casca do painel: barra lateral, cabecalho e area de conteudo.
 *
 * O titulo e a descricao do cabecalho vem da tabela abaixo, indexada pela rota
 * — assim cada tela nao precisa saber como se apresenta no topo.
 */

interface NavEntry {
  to: string;
  label: string;
  icon: Parameters<typeof NavIcon>[0]['name'];
  /** Rotulo da secao que abre acima deste item. */
  section?: string;
  /** Fora da lista para quem nao e administrador. */
  somenteAdmin?: boolean;
}

/**
 * O item so aparece se a tela estiver liberada para a conta.
 *
 * Esconder o item e cortesia, nao protecao: quem digitar a URL cai na guarda de
 * rota, e quem chamar a API direto cai na checagem do servidor.
 */
function itemVisivel(item: NavEntry, session: SessionUser | null | undefined): boolean {
  if (item.somenteAdmin && session?.role !== 'admin') return false;
  const tela = SCREENS.find((screen) => screen.path === item.to);
  if (!tela) return true;
  return telaAtiva(tela.key) && (session?.screens.includes(tela.key) ?? false);
}

/**
 * Navegacao da esquerda: so o trabalho sobre os dados.
 *
 * Usuarios e Sair sairam daqui e vivem no painel da conta, a direita — as duas
 * coisas dizem respeito a quem esta usando o sistema, nao ao que se faz nele.
 */
const NAV: NavEntry[] = [
  { to: '/', label: 'Dashboard', icon: 'home' },
  { to: '/extratos', label: 'Extratos', icon: 'folder' },
  { to: '/classificacao', label: 'Classificação', icon: 'checklist' },
  { to: '/plano', label: 'Plano financeiro', icon: 'plano' },
  { to: '/planilhas', label: 'Planilhas', icon: 'planilha' },
  {
    to: '/bancos-contabeis',
    label: 'Bancos contábeis',
    icon: 'banco',
    section: 'Contabilidade',
  },
  { to: '/patrimoniais', label: 'Contas patrimoniais', icon: 'patrimonio' },
  { to: '/exportacao', label: 'Exportação contábil', icon: 'exportar' },
  { to: '/relatorios', label: 'DRE', icon: 'report', section: 'Relatórios' },
  { to: '/balanco', label: 'Demonstração contábil', icon: 'balanco' },
  { to: '/competencias', label: 'Fechamento', icon: 'lock', section: 'Configuração', somenteAdmin: true },
  { to: '/log', label: 'Log', icon: 'sliders', section: 'Administração', somenteAdmin: true },
];

/**
 * Itens visiveis, com o rotulo de secao reancorado.
 *
 * O rotulo mora no primeiro item da secao. Como a permissao e por tela e nao
 * por secao, esse item pode estar escondido — e ai o rotulo sumiria junto e os
 * itens seguintes apareceriam soltos, sob o titulo da secao anterior. Aqui ele
 * passa para o primeiro item que sobrou.
 */
function navegacaoVisivel(session: SessionUser | null | undefined): NavEntry[] {
  const visiveis: NavEntry[] = [];
  let secaoPendente: string | undefined;

  for (const item of NAV) {
    if (item.section) secaoPendente = item.section;
    if (!itemVisivel(item, session)) continue;
    visiveis.push({ ...item, section: secaoPendente });
    secaoPendente = undefined;
  }

  return visiveis;
}

const PAGE_META: Record<string, { title: string; subtitle: string }> = {
  '/': { title: 'Dashboard', subtitle: 'Indicadores de resultado, custo e recebimento.' },
  '/extratos': { title: 'Extratos', subtitle: 'Importe e acompanhe os arquivos OFX por mês.' },
  '/classificacao': {
    title: 'Classificação',
    subtitle: 'Enquadramento dos lançamentos no plano de contas.',
  },
  '/plano': {
    title: 'Plano financeiro',
    subtitle: 'Grupos e categorias que a classificação oferece.',
  },
  '/planilhas': {
    title: 'Planilhas',
    subtitle: 'Documentos que embasam a emissão das notas fiscais.',
  },
  '/bancos-contabeis': {
    title: 'Bancos contábeis',
    subtitle: 'O código de cada banco no plano de contas da contabilidade.',
  },
  '/patrimoniais': {
    title: 'Contas patrimoniais',
    subtitle: 'Contas de balanço usadas como contrapartida na provisão.',
  },
  '/exportacao': {
    title: 'Exportação contábil',
    subtitle: 'Os lançamentos classificados em partidas dobradas, prontos para o contábil.',
  },
  '/relatorios': { title: 'DRE', subtitle: 'Demonstrativo de resultado por competência.' },
  '/balanco': { title: 'Demonstração contábil', subtitle: 'Balanço patrimonial e demonstrações a partir do balancete.' },
  '/competencias': { title: 'Fechamento', subtitle: 'Encerramento de competências já entregues ao cliente.' },
  '/log': { title: 'Log', subtitle: 'Registro de auditoria: cada ação e seu responsável.' },
  '/usuarios': { title: 'Usuários', subtitle: 'Contas com acesso ao painel.' },
  '/bancos': { title: 'Bancos', subtitle: 'Cada banco é um contexto financeiro separado.' },
  '/perfil': { title: 'Meu perfil', subtitle: 'Seus dados de acesso.' },
};

export function AppShell() {
  const [drawerAberto, setDrawerAberto] = useState(false);
  const { data: session } = useSession();
  const logout = useLogout();
  const feedback = useFeedback();
  const navigate = useNavigate();
  const location = useLocation();

  const meta = PAGE_META[location.pathname] ?? { title: 'Painel', subtitle: '' };
  // As secoes exclusivas de administrador somem junto com seus itens — inclusive
  // os rotulos, que sozinhos anunciariam areas inalcançaveis.
  const navVisivel = navegacaoVisivel(session);
  const trocandoDeTela = useTransicaoDeTela(location.pathname);

  async function handleLogout() {
    const confirmed = await feedback.confirm({
      title: 'Sair do painel?',
      detail: 'Você precisará entrar novamente com e-mail e senha.',
      confirmLabel: 'Sair',
    });
    if (!confirmed) return;

    try {
      await logout.mutateAsync();
      void navigate('/login', { replace: true });
    } catch {
      // Mesmo se a chamada falhar, o cookie local pode ja ter ido embora;
      // mandar para o login e o comportamento seguro.
      feedback.warning('Sessão encerrada localmente.', 'O servidor não confirmou a saída.');
      void navigate('/login', { replace: true });
    }
  }

  return (
    <div className={s.app}>
      <aside className={s.sidebar}>
        <div className={s.brand}>
          {/* Só a marca, centralizada. O nome escrito ao lado era redundante
              com o próprio símbolo, e o alt vazio daria uma sidebar sem
              nenhuma identificação textual para leitor de tela. */}
          <img className={s.brandLogo} src="/assets/apen-logo.png" alt="Apen Capital" />
        </div>

        <nav className={s.nav}>
          {navVisivel.map((item) => (
            <div key={item.to} style={{ display: 'contents' }}>
              {item.section ? <div className={s.sectionLabel}>{item.section}</div> : null}
              <NavLink
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `${s.navItem} ${isActive ? s.navItemActive : ''}`.trim()
                }
              >
                <NavIcon name={item.icon} />
                <span>{item.label}</span>
              </NavLink>
            </div>
          ))}
        </nav>

        {/* Assinatura do escritório, no pé da coluna. `margin-top: auto`
            empurra para baixo, então ela fica no fim mesmo quando a navegação
            é curta, sem depender da altura da lista. */}
        <div className={s.rodape}>
          <img
            className={s.rodapeLogo}
            src="/assets/office-logo.png"
            alt="OFFICE Contadores Associados"
          />
        </div>
      </aside>

      <main className={s.content}>
        <header className={s.topbar}>
          <div className={s.topbarLeft}>
            <h1>{meta.title}</h1>
            <p>{meta.subtitle}</p>
          </div>

          <div className={s.topbarRight}>
            <button
              type="button"
              className={s.userChip}
              aria-haspopup="dialog"
              aria-expanded={drawerAberto}
              onClick={() => {
                setDrawerAberto(true);
              }}
            >
              <Avatar
                className={s.avatar}
                src={session?.avatar_path}
                nome={session?.name}
                tamanho={30}
              />
              <span className={s.userChipText}>
                <span className={s.userChipName}>{session?.name ?? '—'}</span>
                <span className={s.userChipRole}>
                  {session?.role === 'admin' ? 'Administrador' : 'Colaborador'}
                </span>
              </span>
              <svg className={s.userChipCaret} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
          </div>
        </header>

        {/* O limite de Suspense fica aqui, e não em volta das rotas: assim a
            troca de tela só substitui o conteúdo, mantendo barra lateral e
            cabeçalho no lugar. O loader é contido — `.scroll` é position:
            relative — e cobre apenas esta área, como no protótipo. */}
        <div className={s.scroll}>
          {/* Sem conta sem banco aqui: o aviso agora fica em cada tela que
              depende de banco, com o texto certo para o que ela faz. A casca
              nao sabe mais de banco nenhum. */}
          <Suspense fallback={<LiquidLoader />}>
            <Outlet />
          </Suspense>
          {trocandoDeTela ? <LiquidLoader /> : null}
        </div>
      </main>

      <AccountDrawer
        usuario={session}
        aberto={drawerAberto}
        onFechar={() => {
          setDrawerAberto(false);
        }}
        onSair={() => {
          setDrawerAberto(false);
          void handleLogout();
        }}
      />
    </div>
  );
}
